import { Dialog, DialogContent, IconButton } from "@mui/material";
import { usePreventScroll } from "@react-aria/overlays";
import { useProviderSearchContext } from "context/ProviderSearchContext";
import LinkV2, {
  ExternalLinkType,
  useExternalLinkLabel,
} from "ds_legacy/components/LinkV2";
import { HorizontalLayout, VerticalLayout } from "ds_legacy/materials/layouts";
import { dp, margin, padding, sizing } from "ds_legacy/materials/metrics";
import {
  Body,
  Caption,
  FONT_SIZE_12,
  FONT_SIZE_14,
  FONT_WEIGHT_SEMI_BOLD,
} from "ds_legacy/materials/typography";
import { useMedia } from "dsl/atoms/ResponsiveMedia";
import { useFocusElement } from "dsl/hooks/useFocusElement";
import { XIcon } from "lucide-react";
import { CSSProperties, ReactNode } from "react";
import { emailDef } from "react-forms-state";
import { useTranslations } from "translations";
import { TranslationComposition } from "translations/helpers";

export const MODAL_MIN_WIDTH = dp(530);
const MODAL_PADDING_CLOSE_BUTTON = padding(2, 3, 1);
const MODAL_PADDING_CONTENT = padding(0, 3, 2);

export const PROVIDER_SEARCH_LOGIN_MODAL = 1;
export const PROVIDER_SEARCH_LOGIN_MODAL_SUCCESS = 2;
export const PROVIDER_SEARCH_SIGNUP_MODAL = 3;
export const PROVIDER_SEARCH_SIGNUP_MODAL_SUCCESS = 4;

export type ProviderSearchLoginModalType =
  | typeof PROVIDER_SEARCH_LOGIN_MODAL
  | typeof PROVIDER_SEARCH_LOGIN_MODAL_SUCCESS
  | typeof PROVIDER_SEARCH_SIGNUP_MODAL
  | typeof PROVIDER_SEARCH_SIGNUP_MODAL_SUCCESS
  | null;

export const CloseIconButton = ({
  isDisabled,
  onClose,
}: {
  isDisabled?: boolean;
  onClose: () => void;
}) => {
  const translations = useTranslations();

  return (
    <HorizontalLayout
      justify="flex-end"
      margin={margin(0, 0, -1, 0)}
      overflow="visible"
      padding={MODAL_PADDING_CLOSE_BUTTON}
    >
      <IconButton
        aria-label={translations.actions.close}
        data-testid="close-modal"
        disabled={isDisabled}
        size="medium"
        sx={(theme) => ({
          boxSizing: "border-box",
          color: theme.palette.common.black,
        })}
        onClick={onClose}
      >
        <XIcon size={16} />
      </IconButton>
    </HorizontalLayout>
  );
};

export const CaptionWithLink = ({
  href,
  message,
  textAlign,
  type = "website",
}: {
  href: string;
  message: string;
  textAlign?: CSSProperties["textAlign"];
  type?: ExternalLinkType;
}) => {
  const externalLinkLabel = useExternalLinkLabel();

  return (
    <Caption
      fontSize={FONT_SIZE_12}
      margin={margin(0)}
      textAlign={textAlign}
      whiteSpace="normal"
    >
      <TranslationComposition translations={message} withOptions>
        {([before, link, after]) => (
          <>
            {before}
            <LinkV2
              aria-label={externalLinkLabel(link, type)}
              data-testid="caption-link"
              href={href}
              target="_blank"
            >
              {link}
            </LinkV2>
            {after}
          </>
        )}
      </TranslationComposition>
    </Caption>
  );
};

export function ProviderSearchDialog({
  ariaLive,
  children,
  focusDelay,
  onClose,
  paperStyle = {},
  style,
  testId,
  withFullScreenOverride,
}: {
  ariaLive?: "polite" | undefined;
  children: ReactNode;
  focusDelay?: number;
  onClose?: () => void;
  paperStyle?: CSSProperties;
  style?: CSSProperties;
  testId?: string;
  withFullScreenOverride?: boolean;
}) {
  usePreventScroll();
  const focusElement = useFocusElement<HTMLDivElement>({
    delay: focusDelay,
    fallbackRestoreRef: document.querySelector("h1")!,
    focusFirstInnerElement: true,
    restoreFocus: true,
    setTabIndexFallbackRestoreRef: true,
  });
  const { isMobile } = useMedia();
  const fullScreen =
    withFullScreenOverride !== undefined ? withFullScreenOverride : isMobile;

  function handleClose() {
    onClose?.();
  }

  return (
    <Dialog
      ref={focusElement as any}
      aria-live={ariaLive}
      data-testid={testId}
      disableAutoFocus
      disableEscapeKeyDown // this is necessary because of the usage of the ComboBox component inside the Dialogs. When pressing enter, the ComboBox's value container should close, not the dialog
      disableRestoreFocus
      fullScreen={fullScreen}
      open
      PaperProps={{
        "aria-modal": "true",
        sx: {
          boxSizing: "border-box",
          maxWidth: "100%",
          ...paperStyle,
        },
      }}
      sx={{ ...style }}
      transitionDuration={0}
      onClose={handleClose}
    >
      <CloseIconButton onClose={handleClose} />
      {children}
    </Dialog>
  );
}

export const DialogContentWrapper = ({
  children,
  sx,
  testId,
}: {
  children: ReactNode;
  sx?: CSSProperties;
  testId: string;
}) => {
  return (
    <DialogContent
      data-testid={testId}
      sx={{
        alignItems: "center",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
        padding: MODAL_PADDING_CONTENT,
        ...sx,
      }}
    >
      {children}
    </DialogContent>
  );
};

export const providerSearchEmailDef = (elementName: string) => {
  return emailDef(elementName, {
    allowSpecialChars: true,
    customTranslation: (translations) =>
      translations.providersearch.loginModal.createAccount.emailErrorNotEmail,
    fieldRequired: true,
  });
};

export const ProviderRegistrationInfo = ({
  isAligned,
}: {
  isAligned?: boolean;
}) => {
  const translations = useTranslations();
  const externalLinkLabel = useExternalLinkLabel();
  const { config } = useProviderSearchContext();

  return (
    <VerticalLayout
      aligned={isAligned}
      gap={sizing(1)}
      margin={margin(1, 0)}
      overflow="visible"
    >
      <Body
        as="p"
        fontSize={FONT_SIZE_14}
        fontWeight={FONT_WEIGHT_SEMI_BOLD}
        margin={margin(0)}
      >
        {translations.providersearch.loginDropDown.providersTitle}
      </Body>
      <Body as="p" fontSize={FONT_SIZE_14} margin={margin(0)}>
        <TranslationComposition
          translations={
            translations.providersearch.loginDropDown.providersInformation
          }
          withOptions
        >
          {([before, _link, after]) => (
            <>
              {before}
              <LinkV2
                aria-label={externalLinkLabel(config.emailAddress, "email")}
                href={`mailto:${config.emailAddress}`}
                sx={{ display: "block", fontSize: FONT_SIZE_14 }}
              >
                {config.emailAddress}
              </LinkV2>
              {after}
            </>
          )}
        </TranslationComposition>
      </Body>
    </VerticalLayout>
  );
};
